import { SelectedPurposeOfUseOrContact } from '../../common/enums';
import { createBillingAccount, fetchBillingAccounts, fetchContacts, postChangeOwnerAccount } from '../../common/fetch';
import {
  CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE,
  getEmptyBillingAccount,
} from '../../common/utils/billingAccountUtils';
import { BillingAccountDeliveryMethod } from '../../generated/api/billingAccountDeliveryMethod';
import { SourceSystem } from '../../generated/api/sourceSystem';
import { SubscriptionPbxDetails } from '../../generated/api/subscriptionPbxDetails';
import { SubscriptionType } from '../../generated/api/subscriptionType';
import {
  changeNumberPrivacyPublicToCustom,
  getPbxDirectoryDetails,
  getVoiceDirectoryDetails,
} from '../UserAndNumberPublicity/UserNumberPublicityUtils.js';
import type {
  DirectoryListingCorporateNumberPublicity,
  DirectoryListingMobileNumberPublicity,
  PurposeOfUseOrContact,
} from '../../common/types/subscription';
import type { BillingAccount } from '../../generated/api/billingAccount';
import type { BillingAccountSearchResponse } from '../../generated/api/billingAccountSearchResponse';
import type { ChangeOwnerError } from '../../generated/api/changeOwnerError';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { DirectoryDetails } from '../../generated/api/directoryDetails';
import type { GenericError } from '../../generated/api/genericError';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionAction } from '../../generated/api/subscriptionAction';
import type { SubscriptionChangeOwnerAccountRequest } from '../../generated/api/subscriptionChangeOwnerAccountRequest';

// Even thought this is not optimal, the subscription name is currently the only way to identify Liikkuva Wifi subs
export const isLiikkuvaWifiSubscription = (subscriptionName?: string): boolean =>
  /\bliikkuva\b.*\bwifi\b/i.test(subscriptionName || '');

export const isOneTimeFeeDevice = (subscription: Subscription) =>
  subscription.subscriptionType === SubscriptionType.DEVICE &&
  subscription.details?.contractTerm === 1 &&
  !!subscription.details.oneTimeCharge &&
  subscription.details.oneTimeCharge > 0;

export enum ScheduleChangeType {
  NOW = 'NOW',
  FUTURE = 'FUTURE',
}

export interface BillingAccountAndUserInfoFormValues {
  changeContractOwner: {
    targetBillingAccount: string;
    targetContact: string;
    targetPurposeOfUse?: string;
  };
  key: number;
  selected?: SelectedPurposeOfUseOrContact;
  contactId?: string;
  purposeOfUse?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  costCenter?: string;
  employeeNumber?: string;
  directoryListingMobileNumberPublicity?: DirectoryListingMobileNumberPublicity;
  directoryListingCorporateNumberPublicity?: DirectoryListingCorporateNumberPublicity;
  numberPrivacyMobile?: DirectoryDetails.NumberDirectoryEnum;
  numberPrivacyCorporate?: DirectoryDetails.NumberDirectoryEnum;
  billReceiverSelection?: string;
  billingAccount?: BillingAccount;
  scheduledChange?: { change: ScheduleChangeType; date?: string };
}

export const USER_INFO_FIELD_NAMES: Array<keyof BillingAccountAndUserInfoFormValues> = [
  'contactId',
  'purposeOfUse',
  'firstName',
  'lastName',
  'phoneNumber',
  'email',
  'costCenter',
  'employeeNumber',
];

export const buildChangeOwnerRequest = (
  values: BillingAccountAndUserInfoFormValues,
  targetMdmId: string | undefined,
  billingAccountId: string,
  effectiveDate?: number
): SubscriptionChangeOwnerAccountRequest => {
  const contactId = values.selected === SelectedPurposeOfUseOrContact.CONTACT ? values.contactId : undefined;
  const purposeOfUse = values.selected === SelectedPurposeOfUseOrContact.PURPOSEOFUSE ? values.purposeOfUse : undefined;

  return {
    targetAccount: targetMdmId || '',
    targetBillingAccount: billingAccountId,
    targetContactDetails: { contactId, purposeOfUse },
    costCenter: values.costCenter,
    subscriptionReference: values.employeeNumber,
    voiceDirectoryDetails: values.directoryListingMobileNumberPublicity?.voiceDirectoryDetails,
    pbxDirectoryDetails: values.directoryListingCorporateNumberPublicity?.pbxDirectoryDetails,
    effectiveDate,
  };
};

export interface CreateBillingAccountWithContactsResult {
  success: boolean;
  billingAccountId?: string;
  billingAccountDisplayId?: string;
  error?: string;
}

export interface ResolvedBillingAccount {
  billingAccountId: string;
  billingAccountDisplayId?: string;
}

export const loadBillingAccountsContacts = async (targetAccount: string) => {
  const [billingAccountsResponse, contactsResponse] = await Promise.all([
    fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, { mdmId: targetAccount }),
    fetchContacts({ offset: 0 }, { mdmId: targetAccount }),
  ]);
  return {
    billingAccountsResponse,
    contactsResponse: contactsResponse.contacts,
  };
};

export const createNewBillingAccount = async (
  billingAccountData: BillingAccount,
  targetMdmId?: string
): Promise<CreateBillingAccountWithContactsResult> => {
  try {
    const createBaResponse = await createBillingAccount(billingAccountData, targetMdmId);

    return {
      success: true,
      billingAccountId: createBaResponse.billingAccountId,
      billingAccountDisplayId: createBaResponse.billingAccountDisplayId,
    };
  } catch (error) {
    return {
      success: false,
      error: 'Failed to create billing account',
    };
  }
};

/**
 * Returns the corporate number privacy setting for MOBILE subscriptions.
 * Only applicable when there is no PBX type, or the PBX type is RING/VAKIO.
 */
export const getNumberPrivacyCorporate = (
  subscription: Subscription,
  purposeOfUseOrContact: PurposeOfUseOrContact
): DirectoryDetails.NumberDirectoryEnum | undefined => {
  if (subscription.subscriptionType !== SubscriptionType.MOBILE) {
    return undefined;
  }

  const corporatePublicity = purposeOfUseOrContact.directoryListingCorporateNumberPublicity;
  if (!corporatePublicity) {
    return undefined;
  }

  const pbxType = subscription.details?.mobile?.pbxConfiguration?.pbxConfigurationDetails.pbxType;
  const isSupportedPbxType =
    !pbxType ||
    pbxType === SubscriptionPbxDetails.PbxTypeEnum.RING ||
    pbxType === SubscriptionPbxDetails.PbxTypeEnum.VAKIO;

  return isSupportedPbxType ? corporatePublicity.pbxDirectoryDetails?.numberDirectory : undefined;
};

export const getNumberPrivacyMobile = (
  subscription: Subscription,
  purposeOfUseOrContact: PurposeOfUseOrContact
): DirectoryDetails.NumberDirectoryEnum | undefined => {
  if (subscription.subscriptionType !== SubscriptionType.MOBILE) {
    return undefined;
  }
  return purposeOfUseOrContact.directoryListingMobileNumberPublicity?.voiceDirectoryDetails?.numberDirectory;
};

export const getDefaultFormValues = (
  subscription: Subscription,
  purposeOfUseOrContact: PurposeOfUseOrContact,
  companyInfo?: CompanyInfoResponse
): BillingAccountAndUserInfoFormValues => {
  const selectedDefaultValue =
    purposeOfUseOrContact.purposeOfUse && !purposeOfUseOrContact.contactId
      ? SelectedPurposeOfUseOrContact.PURPOSEOFUSE
      : SelectedPurposeOfUseOrContact.CONTACT;

  const numberPrivacyMobile = changeNumberPrivacyPublicToCustom(
    getNumberPrivacyMobile(subscription, purposeOfUseOrContact)
  );
  const numberPrivacyCorporate = changeNumberPrivacyPublicToCustom(
    getNumberPrivacyCorporate(subscription, purposeOfUseOrContact)
  );

  return {
    changeContractOwner: {
      targetBillingAccount: subscription.billingAccountId || '',
      targetContact: '',
      targetPurposeOfUse: subscription.subscriptionPurposeOfUse,
    },
    key: 0,
    // User info fields at root level
    selected: purposeOfUseOrContact.selected ?? selectedDefaultValue,
    contactId: purposeOfUseOrContact.contactId,
    purposeOfUse: purposeOfUseOrContact.contactId ? undefined : purposeOfUseOrContact.purposeOfUse,
    firstName: purposeOfUseOrContact.firstName,
    lastName: purposeOfUseOrContact.lastName,
    phoneNumber: purposeOfUseOrContact.phoneNumber,
    email: purposeOfUseOrContact.email,
    costCenter: purposeOfUseOrContact.costCenter,
    employeeNumber: purposeOfUseOrContact.employeeNumber,
    directoryListingMobileNumberPublicity: getVoiceDirectoryDetails(purposeOfUseOrContact, companyInfo),
    directoryListingCorporateNumberPublicity: getPbxDirectoryDetails(purposeOfUseOrContact, companyInfo),
    numberPrivacyMobile,
    numberPrivacyCorporate,
    scheduledChange: { change: ScheduleChangeType.NOW },
    // Billing account default values
    billReceiverSelection: 'SAME_AS_CONTACT',
    billingAccount: {
      ...getEmptyBillingAccount(companyInfo?.companyName || ''),
      payerAddress: {
        line1: companyInfo?.address?.line1 || '',
        postalCode: companyInfo?.address?.postalCode || '',
        postOffice: companyInfo?.address?.postOffice || '',
        countryCode: 'FIN',
      },
      billingContactId: '',
      billFormatType: 'CONSOLIDATED_INVOICE_WITH_ITEMIZATION',
      deliveryMethod: BillingAccountDeliveryMethod.ELECTRONIC,
      billLanguage: 'FI',
      billElectronicOperator: undefined,
      billingAccountName: companyInfo?.companyName || '',
    },
  };
};

export const getOrCreateBillingAccount = async (
  values: BillingAccountAndUserInfoFormValues,
  targetMdmId?: string,
  billingAccounts?: BillingAccountSearchResponse[]
): Promise<ResolvedBillingAccount> => {
  const selectedBillingAccountId = values.changeContractOwner.targetBillingAccount;
  if (selectedBillingAccountId !== CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE) {
    return {
      billingAccountId: selectedBillingAccountId,
      billingAccountDisplayId: billingAccounts?.find(ba => ba.result.billingAccountId === selectedBillingAccountId)
        ?.result.billingAccountDisplayId,
    };
  }
  if (!values.billingAccount) {
    throw new Error('Billing account information is missing');
  }
  const result = await createNewBillingAccount(values.billingAccount, targetMdmId);
  if (!result.success || !result.billingAccountId) {
    throw new Error(result.error || 'Failed to create billing account');
  }

  return { billingAccountId: result.billingAccountId, billingAccountDisplayId: result.billingAccountDisplayId };
};

const unexpectedErrorResponse = {
  commonErrorResponse: { errorType: 'UNEXPECTED_ERROR', message: 'Unexpected error occurred' } as GenericError,
};

export const postChangeContractOwner = async (
  subscriptionId: string,
  request: SubscriptionChangeOwnerAccountRequest,
  mdmId?: string
): Promise<{
  subscriptionAction?: SubscriptionAction;
  changeOwnerErrorResponse?: ChangeOwnerError;
  commonErrorResponse?: GenericError;
}> => {
  try {
    const response = await postChangeOwnerAccount(subscriptionId, request, mdmId);
    const subscriptionAction = await response.json();
    return { subscriptionAction };
  } catch (error) {
    if (error instanceof Response) {
      try {
        const errorResponse = await error.json();
        if (errorResponse[0]?.reason) {
          return { changeOwnerErrorResponse: errorResponse[0] };
        }
      } catch {
        return unexpectedErrorResponse;
      }
    }
    return unexpectedErrorResponse;
  }
};

export const getDirectoryListingMobileNumberPublicity = (
  inputSubscription: Subscription
): DirectoryListingMobileNumberPublicity | undefined => {
  const mobile = inputSubscription?.details?.mobile;
  if (mobile?.directoryListing) {
    return {
      voiceDirectoryDetails: {
        address: mobile.directoryAddress,
        companyName: mobile.companyName,
        numberDirectory: mobile.directoryListing,
        recipientName: mobile.recipientName,
      },
    };
  } else {
    return undefined;
  }
};

export const getDirectoryListingCorporateNumberPublicity = (
  subscription: Subscription
): DirectoryListingCorporateNumberPublicity | undefined => {
  const pbxDetails = subscription?.details?.mobile?.pbxConfiguration?.pbxConfigurationDetails;
  const corporateDirectory = pbxDetails?.corporateNumberDirectoryDetails;

  const isSupportedPbxType =
    pbxDetails?.pbxType === SubscriptionPbxDetails.PbxTypeEnum.RING ||
    pbxDetails?.pbxType === SubscriptionPbxDetails.PbxTypeEnum.VAKIO;

  if (!corporateDirectory || !isSupportedPbxType) {
    return undefined;
  } else {
    return {
      pbxDirectoryDetails: {
        address: corporateDirectory.address,
        companyName: corporateDirectory.companyName,
        numberDirectory: corporateDirectory.numberDirectory,
        recipientName: corporateDirectory.recipientName,
      },
    };
  }
};

export const createPurposeOfUseObj = (subscription: Subscription): PurposeOfUseOrContact => ({
  contactId: subscription.subscriptionContactId,
  contactName: subscription.subscriptionContactName,
  directoryListingMobileNumberPublicity: getDirectoryListingMobileNumberPublicity(subscription),
  directoryListingCorporateNumberPublicity: getDirectoryListingCorporateNumberPublicity(subscription),
  purposeOfUse: subscription.subscriptionPurposeOfUse,
  costCenter: subscription.costCenter,
  employeeNumber: subscription.subscriptionReference,
});
